import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, ShieldCheck, Globe, Code2, FileText, FileDown, Table2, BarChart3, History, ClipboardCheck,
  Download, Mail, BookOpen, Eye, ArrowRight, Hourglass,
} from "lucide-react";
import api, { API, getToken } from "@/lib/api";
import { OUTCOMES } from "@/lib/assessmentData";

const GROUPS = [
  { id: "participants", label: "Participants", icon: Users, blurb: "What people taking part in a simulation can see." },
  { id: "admin", label: "Admin / facilitator only", icon: ShieldCheck, blurb: "Visible only to you. Use these for debriefs and your recommendations work." },
  { id: "public", label: "Prospective clients (public)", icon: Globe, blurb: "What organisations see before they work with you." },
  { id: "internal", label: "Internal / technical", icon: Code2, blurb: "Reference material for developers and technical stakeholders." },
];

const REPORTS = [
  { group: "participants", key: "participant", icon: FileText, title: "Participant debrief report", format: "Web page · PDF",
    who: "Each participant (their own, after the session ends) · You (anyone's)",
    includes: ["Session, scenario, organisation, date and completion %", "Factual executive summary (no AI, no scoring)", "Every inject with their response and key resilience considerations", "Participation summary, personal actions and their session feedback"],
    where: "Participants: 'View my debrief report' on the ended session. You: Sessions → Participant reports, or below." },
  { group: "participants", key: "history", icon: History, title: "My response history", format: "In the simulation",
    who: "Each participant, during the session",
    includes: ["Every answer they have submitted so far, per inject"],
    where: "Inside the live simulation (participant view)." },
  { group: "participants", key: "shared-analysis", icon: BarChart3, title: "Shared analysis", format: "In the simulation",
    who: "Participants, only when you switch it on",
    includes: ["Phrases used by two or more roles", "Checklist items nobody mentioned", "Clashes between competing actions"],
    where: "Control room → Share analysis toggle." },
  { group: "admin", key: "session-pdf", icon: FileText, title: "Whole-session debrief (Session PDF)", format: "PDF",
    who: "You only",
    includes: ["Trigger, every inject and task", "Response counts plus matches, gaps and clashes per inject", "All questionnaire answers for the session"],
    where: "Sessions → Session PDF, or below. Basis for your recommendations report." },
  { group: "admin", key: "session-csv", icon: Table2, title: "Session responses (CSV)", format: "CSV spreadsheet",
    who: "You only",
    includes: ["One row per response: inject, task, role, user, table, response, late flag, time"],
    where: "Sessions → CSV, or below." },
  { group: "admin", key: "analysis", icon: BarChart3, title: "Live analysis", format: "In the control room",
    who: "You (participants only if shared)",
    includes: ["Matches, missed checklist items and clashes for each inject, on demand"],
    where: "Control room → Run analysis." },
  { group: "admin", key: "export-all", icon: FileDown, title: "Export all data", format: "Excel · PDF",
    who: "You only",
    includes: ["Companies, users, sessions, responses, notes, inbox messages, questionnaire and analysis across everything"],
    where: "Companies → Export all, or below." },
  { group: "admin", key: "assessments", icon: ClipboardCheck, title: "Self-assessment submissions", format: "Admin table · email copy",
    who: "You only",
    includes: ["Contact details, all 8 answers, outcome and the reasons shown to the person", "A copy of each is emailed to support@tardigradeteam.com"],
    where: "Assessments tab." },
  { group: "public", key: "assessment-result", icon: ClipboardCheck, title: "Self-assessment result", format: "Web page",
    who: "Anyone visiting the website",
    includes: ["One of three outcomes with a plain-language explanation (no scores)", "Next step: discovery or departmental conversation, or free resources"],
    where: "Website → How we work → Take the self-assessment (/assessment)." },
  { group: "public", key: "resources", icon: Download, title: "Free resource PDFs", format: "4 fillable PDFs",
    who: "Organisations whose result is 'Not currently suitable' · anyone you send them to",
    includes: OUTCOMES.not_suitable.resources.map((r) => r.title),
    where: "Assessment result page and the Assessments tab." },
  { group: "public", key: "support-guide", icon: Mail, title: "Support guide", format: "Email",
    who: "Client contacts you send it to",
    includes: ["Getting started, how to sign up, troubleshooting and links to your policies"],
    where: "All Comms → Support guide → Send to client." },
  { group: "internal", key: "arch", icon: BookOpen, title: "Architecture diagrams", format: "PDF · 2 pages",
    who: "Developers and technical stakeholders",
    includes: ["Request and data-flow diagram", "Database relationship diagram (15 collections)"],
    where: "General architecture tab." },
  { group: "internal", key: "ops", icon: BookOpen, title: "Backend operations & data reference", format: "PDF",
    who: "Developers and technical stakeholders",
    includes: ["Every backend area, endpoint, security measure and data collection"],
    where: "General architecture tab." },
];

const PLANNED = [
  { title: "Recommendations report", desc: "A client-facing report built from the whole-session debrief." },
  { title: "Action recording", desc: "Participants record actions (owner, due date, status) that fill the Personal actions section." },
  { title: "AI-generated summaries", desc: "An optional summary slot is already reserved in the participant report." },
];

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/15 text-[var(--txt-secondary)] hover:bg-white/5 hover:text-white transition-colors";
const linkBtn = (props, children) => <a {...props} className={btn}>{children}</a>;

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className="bg-[var(--bg-elevated)] border border-white/15 px-3 py-1.5 text-sm text-white outline-none focus:border-white max-w-[260px]">
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
  </select>
);

function ParticipantPicker({ sid }) {
  const [people, setPeople] = useState([]);
  const [uid, setUid] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    setUid(""); setPeople([]);
    if (sid) api.get(`/sessions/${sid}/participants`).then((r) => setPeople(r.data)).catch(() => {});
  }, [sid]);
  if (!sid) return <span className="text-sm text-[var(--txt-muted)]">Pick a session above to open a participant's report.</span>;
  return (
    <>
      <Select testid="catalogue-participant-select" value={uid} onChange={setUid} placeholder="Choose participant"
        options={people.map((p) => ({ id: p.id, label: `${p.name || p.email} · ${p.responses} resp.` }))} />
      {uid && <>
        <button data-testid="catalogue-view-participant" onClick={() => nav(`/report/${sid}?user=${uid}`)} className={btn}><Eye className="w-3.5 h-3.5" /> View</button>
        {linkBtn({ "data-testid": "catalogue-pdf-participant", href: `${API}/sessions/${sid}/participant-report.pdf?user_id=${uid}&token=${getToken()}` }, <><Download className="w-3.5 h-3.5" /> PDF</>)}
      </>}
    </>
  );
}

function CardActions({ k, sid, goTab }) {
  const nav = useNavigate();
  const token = getToken();
  const needSession = <span className="text-sm text-[var(--txt-muted)]">Pick a session above.</span>;
  const go = (tab, label) => <button data-testid={`catalogue-go-${k}`} onClick={() => goTab(tab)} className={btn}>{label} <ArrowRight className="w-3.5 h-3.5" /></button>;
  switch (k) {
    case "participant": return <ParticipantPicker sid={sid} />;
    case "session-pdf": return sid ? linkBtn({ "data-testid": "catalogue-session-pdf", href: `${API}/sessions/${sid}/report.pdf?token=${token}` }, <><Download className="w-3.5 h-3.5" /> Download Session PDF</>) : needSession;
    case "session-csv": return sid ? linkBtn({ "data-testid": "catalogue-session-csv", href: `${API}/sessions/${sid}/export.csv?token=${token}` }, <><Download className="w-3.5 h-3.5" /> Download CSV</>) : needSession;
    case "analysis":
    case "shared-analysis":
    case "history": return sid ? <button data-testid={`catalogue-open-sim-${k}`} onClick={() => nav(`/sim/${sid}`)} className={btn}>Open control room <ArrowRight className="w-3.5 h-3.5" /></button> : needSession;
    case "export-all": return <>
      {linkBtn({ "data-testid": "catalogue-export-xlsx", href: `${API}/admin/export-all.xlsx?token=${token}` }, <><Download className="w-3.5 h-3.5" /> Excel</>)}
      {linkBtn({ "data-testid": "catalogue-export-pdf", href: `${API}/admin/export-all.pdf?token=${token}` }, <><Download className="w-3.5 h-3.5" /> PDF</>)}
    </>;
    case "assessments": return go("assessments", "Open Assessments");
    case "assessment-result": return linkBtn({ "data-testid": "catalogue-open-assessment", href: "/assessment", target: "_blank", rel: "noreferrer" }, <>Open self-assessment <ArrowRight className="w-3.5 h-3.5" /></>);
    case "resources": return OUTCOMES.not_suitable.resources.map((r, i) => <span key={r.file}>{linkBtn({ "data-testid": `catalogue-resource-${i}`, href: r.file, download: true }, <><Download className="w-3.5 h-3.5" /> {r.title}</>)}</span>);
    case "support-guide": return go("comms", "Open All Comms");
    default: return go("dev-architecture", "Open General architecture");
  }
}

const ReportCard = ({ r, sid, goTab }) => (
  <div data-testid={`catalogue-card-${r.key}`} className="elevated p-6 flex flex-col">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-10 h-10 shrink-0 bg-white/5 flex items-center justify-center"><r.icon className="w-5 h-5 text-[var(--txt-secondary)]" /></div>
      <div className="min-w-0">
        <h3 className="font-display font-bold text-lg leading-tight">{r.title}</h3>
        <div className="font-mono text-[var(--txt-muted)] mt-1" style={{ fontSize: "11px" }}>{r.format.toUpperCase()}</div>
      </div>
    </div>
    <dl className="space-y-3 text-sm flex-1">
      <div><dt className="text-[var(--txt-muted)] mb-0.5">Who it's for</dt><dd className="text-white">{r.who}</dd></div>
      <div><dt className="text-[var(--txt-muted)] mb-0.5">What's included</dt>
        <dd><ul className="list-disc pl-4 space-y-0.5 text-[var(--txt-secondary)]">{r.includes.map((i) => <li key={i}>{i}</li>)}</ul></dd></div>
      <div><dt className="text-[var(--txt-muted)] mb-0.5">Where to find it</dt><dd className="text-[var(--txt-secondary)]">{r.where}</dd></div>
    </dl>
    <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-white/10"><CardActions k={r.key} sid={sid} goTab={goTab} /></div>
  </div>
);

export function ReportsCatalogue({ goTab }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState("");
  useEffect(() => { api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {}); }, []);

  return (
    <div className="px-8 py-8 space-y-12" data-testid="reports-catalogue">
      <div className="elevated p-5 flex flex-wrap items-center gap-4 sticky top-0 z-10">
        <div className="text-sm text-[var(--txt-secondary)]">Session for the cards below:</div>
        <Select testid="catalogue-session-select" value={sid} onChange={setSid} placeholder="Choose a session"
          options={sessions.map((s) => ({ id: s.id, label: `${s.name} · ${s.status}` }))} />
        <div className="text-xs text-[var(--txt-muted)]">{REPORTS.length} reports and documents · {PLANNED.length} planned</div>
      </div>
      {GROUPS.map((g) => (
        <section key={g.id} data-testid={`catalogue-group-${g.id}`}>
          <div className="flex items-center gap-3 mb-1"><g.icon className="w-5 h-5 text-[var(--urgent)]" /><h2 className="font-display font-bold text-2xl">{g.label}</h2></div>
          <p className="text-[var(--txt-muted)] mb-5">{g.blurb}</p>
          <div className="grid lg:grid-cols-2 2xl:grid-cols-3 gap-4">
            {REPORTS.filter((r) => r.group === g.id).map((r) => <ReportCard key={r.key} r={r} sid={sid} goTab={goTab} />)}
          </div>
        </section>
      ))}
      <section data-testid="catalogue-planned">
        <div className="flex items-center gap-3 mb-1"><Hourglass className="w-5 h-5 text-[var(--txt-muted)]" /><h2 className="font-display font-bold text-2xl">Planned / coming soon</h2></div>
        <p className="text-[var(--txt-muted)] mb-5">Requested but not built yet.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {PLANNED.map((p) => (
            <div key={p.title} className="border border-dashed border-white/15 p-5">
              <div className="font-medium mb-1">{p.title}</div><p className="text-sm text-[var(--txt-secondary)]">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
